import type { ReactNode } from 'react'

export const inputBase =
  'w-full rounded-xl border border-ink-200 bg-white/80 backdrop-blur-sm px-4 py-3 text-sm text-ink-950 placeholder:text-ink-400 transition-colors focus:outline-none focus:border-accent-500 focus:ring-4 focus:ring-accent-100'

// ── Text field (controlled) ──
export function Field({
  label,
  name,
  type = 'text',
  required,
  placeholder,
  value,
  onChange,
}: {
  label: string
  name: string
  type?: string
  required?: boolean
  placeholder?: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ink-800">
        {label}
        {required && <span className="text-accent-600 ml-0.5">*</span>}
      </span>
      <input
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1.5 ${inputBase}`}
      />
    </label>
  )
}

// ── Select (controlled) ──
export function Select({
  label,
  name,
  options,
  placeholder,
  value,
  onChange,
}: {
  label: string
  name: string
  options: { value: string; label: string }[]
  placeholder: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ink-800">{label}</span>
      <div className="relative mt-1.5">
        <select
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputBase} appearance-none pr-10 ${value ? '' : 'text-ink-400'}`}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="text-ink-950">
              {o.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink-500">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </label>
  )
}

// ── Range slider with a large live value ──
export function RangeSlider({
  label,
  min,
  max,
  step,
  value,
  onChange,
  format,
}: {
  label: string
  min: number
  max: number
  step: number
  value: number
  onChange: (v: number) => void
  format: (v: number, atMax: boolean) => ReactNode
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-semibold text-ink-800">{label}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-3 w-full cursor-pointer accent-accent-500"
        aria-label={label}
      />
      <div className="mt-2 text-2xl font-semibold tracking-[-0.02em] text-accent-700">
        {format(value, value >= max)}
      </div>
    </div>
  )
}

// ── Split slider (home delivery % ↔ pickup-point %) ──
export function SplitSlider({
  label,
  value,
  onChange,
  leftLabel,
  rightLabel,
}: {
  label: string
  value: number // 0–100, left share
  onChange: (v: number) => void
  leftLabel: string
  rightLabel: string
}) {
  return (
    <div>
      <span className="text-xs font-semibold text-ink-800">{label}</span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-3 w-full cursor-pointer accent-accent-500"
        aria-label={label}
      />
      <div className="mt-2 flex items-center justify-between text-sm font-semibold">
        <span className="text-accent-700">
          {leftLabel} {value}%
        </span>
        <span className="text-ink-500">
          {rightLabel} {100 - value}%
        </span>
      </div>
    </div>
  )
}

// ── Single-choice card (sector / sourcing) ──
export function OptionCard({
  label,
  icon,
  selected,
  onClick,
}: {
  label: string
  icon?: ReactNode
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={[
        'group flex w-full flex-col items-center justify-center gap-2 rounded-2xl border px-3 py-4 text-center transition-all',
        selected
          ? 'border-accent-500 bg-accent-50 text-accent-800 shadow-[0_16px_40px_-20px_rgba(59,93,255,0.55)]'
          : 'border-ink-100 bg-ink-100 text-ink-700 hover:border-accent-200 hover:bg-white',
      ].join(' ')}
    >
      {icon && (
        <span className={selected ? 'text-accent-700' : 'text-ink-400 group-hover:text-accent-600'}>
          {icon}
        </span>
      )}
      <span className="text-xs sm:text-sm font-medium leading-tight">{label}</span>
    </button>
  )
}

// ── Toggle chip (zones multi-select) ──
export function Chip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors',
        selected
          ? 'border-accent-600 bg-accent-600 text-white shadow-[0_10px_26px_-12px_rgba(59,93,255,0.6)]'
          : 'border-ink-200 bg-white text-ink-700 hover:border-accent-300 hover:bg-accent-50',
      ].join(' ')}
    >
      {selected && (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
          <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {label}
    </button>
  )
}
